<script>
import headers from './headers';
import api from './api';
import { REQUIREMENT } from './constants';

export default {
    data() {
        return {
            loading: true,
            table: [],

            file: null,
            bulkActionInProgress: false,

            selectedItem: {
                metric: null,
                header: null,
                child: null
            },

            action: {
                option: 0,
                key: null,
            },

            dataForAction: null,

            selectedMultiplesKey: null,

            newItemToCreate: null,

            sortHeader: null,
            sortAscending: true,
        }
    },

    computed: {
        headers() {
            return headers;
        },

        REQUIREMENTS() {
            return REQUIREMENT;
        },

        actionOptions() {
            return {
                none: 0,
                update: 1,
                delete: 2,
                create: 3,
            };
        },

        selectedMetric() {
            if (this.selectedItem.metric == null)
                return null;

            return this.table.find(x => x.id === this.selectedItem.metric.id);
        },

        selectedChildHeader() {
            if (this.selectedItem.metric === null || this.selectedItem.child === null || this.selectedItem.header === null) 
                return null;
        
            return this.headers.find(x => x.key === this.selectedItem.header.key);
        },

        selectedItemId() {
            if (this.selectedItem.metric == null)
                return null;

            return this.action.key === 'metric' ? 
                this.selectedItem.metric.id : 
                (this.selectedItem.child == null ? null : this.selectedItem.child.id);
        },

        refinedMultiplesKey() {
            switch (this.selectedMultiplesKey) {
                case 'related_sdgs':
                    return 'sdg';
                case 'related_departments':
                    return 'department';
                default:
                    return this.selectedMultiplesKey
            }
        },
    },

    async mounted() {
        await this.onMounted();
    },

    methods: {
        async setLoading(b) {
            this.loading = b;
        },

        async onMounted() {
            api.fetch()
                .then(res => {
                    this.table = res.data;
                })
                .catch(() => {
                    this.table = [];
                    this.$notify('info', 'The data does not exist yet. You must set it up before any further changes.', true);
                })
                .finally(() => {
                    this.setLoading(false);
                })
        },

        async setSelectedItem(metric, header, child) {
            this.selectedItem.metric = metric;
            this.selectedItem.header = header;
            this.selectedItem.child = child;
        },

        async handleSelectItem(metric, header, child) {
            await this.setSelectedItem(metric, header, child);
            await this.$refs.optionsPopup.openPopup();
        },
        
        async setSelectedAction(option, key) {
            this.action.option = option;
            this.action.key = key;
        },
        
        async handleSelectAction(option, key) {
            await this.setSelectedAction(option, key);
            if (this.action.option === this.actionOptions.update || this.action.option === this.actionOptions.create) {
                await this.fetchDataForAction();
            } 
            
            if (this.action.option === this.actionOptions.create) {
                await this.setNewItemToCreate();
            }

            await this.$refs.actionPopup.openPopup();
        },

        async fetchDataForAction() {
            if (this.selectedItemId === null) return;
            await this.setLoading(true);
            const res = await api.fetchDataForAction(this.action.key, this.selectedItemId);
            await this.setDataForAction(res.data);
            await this.setLoading(false);
        },

        async setNewItemToCreate() {
            if (this.action.key === 'metric') {
                this.newItemToCreate = {
                    name: '',
                    description: '',
                    category: { id: -1, name: '' },
                    element: { id: -1, name: '' },
                    kpi: { id: -1, name: '' },
                    objective: { id: -1, name: '' },
                    gri: { id: -1, name: '' },
                    related_sdgs: [],
                    sector: { id: -1, name: '' },
                    related_departments: [],
                    gri_requirement: '',
                    tsrs_requirement: '',
                    tsrs_1: '',
                    tsrs_2: '',
                    tsrs_volume: '',
                    unit: '',
                    formula: '',
                    control: '',
                };
            } else  {
                this.newItemToCreate = {
                    name: '',
                    description: '',
                };

                const parent_key = this.selectedChildHeader.metric_update_options.parent_key;
                if (parent_key)
                    this.newItemToCreate[parent_key] = -1;
            }
        },

        async setDataForAction(d) {
            this.dataForAction = d;
        },

        async handleDeleteItem() {
            if (this.selectedItemId === null) return;    
            await this.setLoading(true);
            await api.delete(this.action.key, this.selectedItemId)
                .then(async () => {
                    await this.handlePostItemDelete();
                    this.$notify('success', '', true);
                })
                .catch(() => {
                    this.$notify('error', '', true);
                })
                .finally(async () => {
                    await this.hideAllPopups();
                    await this.setLoading(false);
                })
        },

        async handlePostItemDelete() {
            if (this.action.key === 'metric') {
                this.table = this.table.filter(x => x.id !== this.selectedItemId);
            } else {
                const key = this.selectedItem.header.key;
                this.table = this.table.filter(x => x[key] && x[key].id !== this.selectedItemId);
            }
        },

        async handleUpdateItem() {
            if (this.selectedItemId === null) return;    
            await this.setLoading(true);
            const payload = this.action.key === 'metric' ? this.selectedMetric : this.selectedItem.child;
            await api.update(this.action.key, payload)
                .then(async () => {
                    this.$notify('success', '', true);

                    if (this.action.key !== 'metric') {
                        for (let idx = 0; idx < this.table.length; idx++) {
                            const data = this.table[idx];
                            data[this.selectedChildHeader.key] = payload;
                        }
                    }
                })
                .catch(() => {
                    this.$notify('error', '', true);
                })
                .finally(async () => {
                    await this.hideAllPopups();
                    await this.setLoading(false);
                })
        },

        async handleCreateItem() {
            if (this.newItemToCreate === null) 
                return;    
            await this.setLoading(true);
            await api.create(this.action.key, this.newItemToCreate)
                .then(async (res) => {
                    this.$notify('success', '', true);
                    if (this.action.key === 'metric') {
                        this.newItemToCreate.id = res.data;
                        let element;
                        let category;
                        for (let i = 0; i < this.table.length; i++) {
                            const item = this.table[i];
                            if (item.kpi.id === this.newItemToCreate.kpi.id) {
                                element = item.element;
                                category = item.category;
                                break;
                            }
                        }

                        this.newItemToCreate.element = element;
                        this.newItemToCreate.category = category;

                        this.table.unshift(this.newItemToCreate);
                    }
                })
                .catch(() => {
                    this.$notify('error', '', true);
                })
                .finally(async () => {
                    await this.hideAllPopups();
                    await this.setLoading(false);
                })
        },

        async hideAllPopups() {
            await this.$refs.actionPopup.startHideProcess();
            await this.$refs.optionsPopup.startHideProcess();
        },

        openMultiplesOptions(key) {
            this.selectedMultiplesKey = key;
            this.$refs.updateMultiplesPopup.openPopup();
        },

        handleClickMultiplesOption(opt) {
            const metric = this.action.option === this.actionOptions.update ? this.selectedItem.metric : 
                (this.action.option === this.actionOptions.create ? this.newItemToCreate : null);

            if (!metric || !metric[this.selectedMultiplesKey]) return;

            const idx = metric[this.selectedMultiplesKey].findIndex(x => x.id === opt.id);
            if (idx === -1) {
                metric[this.selectedMultiplesKey].push(opt);
            } else {
                metric[this.selectedMultiplesKey].splice(idx, 1);
            }
        },

        handleFileSelect(event) {
            if (!event || !event.target || !event.target.files || event.target.files.length === 0) return;
            this.file = event.target.files[0];
        },

        async handleBulkUpdate(action) {
                if (this.bulkActionInProgress) return;
            this.bulkActionInProgress = true;

            if (action === 'export') {
                api.export().finally(() => this.bulkActionInProgress = false);
                return;
            }

            if (this.file === null) {
                this.$notify('info', 'Select your XLS file to upload.', true);
                return;
            }
            if (action === 'init') {
                await api.initialize(this.file)
                    .then(async () => {
                        await this.onMounted();
                        await this.$refs.bulkUpdatePopup.startHideProcess();
                        this.file = null;
                    })
                    .catch(() => {
                        this.$notify('error', '', true);
                    })
                    .finally(() => this.bulkActionInProgress = false);
            } else if (action === 'update') {
                await api.bulkUpdate(this.file)
                    .then(async () => {
                        await this.onMounted();
                        await this.$refs.bulkUpdatePopup.startHideProcess();
                        this.file = null;
                    })
                    .catch(() => {
                        this.$notify('error', '', true);
                    })
                    .finally(() => this.bulkActionInProgress = false);
            }
        },

        async handleSort(header) {
            if (!header || this.table.length === 0) return;
            await this.setLoading(true);
            await this.setSortOptions(header);
            const key = header.key;

            let sortType;
            if (!header.is_self_table) {
                sortType = 'basic';
            } else if (!header.is_multiple) {
                sortType = 'deep';
            } else {
                sortType = 'deep_multiple';
            }

            this.table.sort((a, b) => {

                let aVal, bVal;

                switch (sortType) {
                    case 'basic':
                        aVal = String(a[key]);
                        bVal = String(b[key]);
                        break;
                    case 'deep':
                        aVal = String(a[key].name);
                        bVal = String(b[key].name);
                        break;
                    case 'deep_multiple':
                        aVal = a[key].length > 0 ? String(a[key][0].name) : 'Z';
                        bVal = b[key].length > 0 ? String(b[key][0].name) : 'Z';
                        break;
                    default:
                        return 0;
                }

                return this.sortAscending
                    ? bVal.localeCompare(aVal)
                    : aVal.localeCompare(bVal);
            });

            await this.setLoading(false);
        },

        async setSortOptions(header) {
            if (header === this.sortHeader) {
                this.sortAscending = !this.sortAscending;
            } else {
                this.sortAscending = true;
            }
            this.sortHeader = header;
        },
    }
}
</script>

<template>
    <div id="metricsWrapper">
        <div v-if="loading" 
            class="flex-all-centered-row" 
            style="height: 500px !important;"
        >
            <loading-spinner fontSize="2em" />
        </div>

        <div v-if="!loading && table.length > 0" class="flex-all-centered-row" style="justify-content: flex-start; padding: 1em 0;">
            <button class="sust-btn sust-btn-green"
                @click="$refs.bulkUpdatePopup.openPopup()"
            >
                Bulk Update Menu
            </button>
        </div>

        <div v-else-if="!loading && table.length === 0"
            class="flex-all-centered-row" style="min-height: 200px;"
        >
            <button class="btn-pretend" @click="$refs.bulkUpdatePopup.openPopup()">
                The data does not exist yet. You must set it up before any further changes.
            </button>
        </div>

        <div v-show="!loading && table.length > 0" id="metricsTable">
            <div class="row header">
                <div v-for="header in headers" :key="header.key"
                    class="cell"
                    @click="handleSort(header)"
                >
                    {{ $t(`metrics_admin_panel.${header.key}.title`) }}
                </div>
            </div>

            <!-- each metric row from here on out -->
            <div v-for="metric in table" :key="metric.id"
                class="row"
            >
                <div v-for="header in headers" :key="header.key"
                    class="cell" tabindex="0"
                    @click="handleSelectItem(metric, header, metric[header.key])"
                >
                    <div v-if="header.is_self_table && !header.is_multiple">
                        {{ metric[header.key].name }}
                    </div>
                    
                    <div v-if="header.is_self_table && header.is_multiple"
                        class="flex-all-centered-col"
                    >
                        <div v-for="(obj, obj_idx) in metric[header.key]" :key="obj.id">
                            {{ obj.name }}
                            <span v-if="obj_idx < metric[header.key].length -1">
                                ,
                            </span>
                        </div>
                    </div>
                    
                    <div v-if="!header.is_self_table && !header.is_multiple">
                        <div v-if="header.key.includes('_requirement')">
                            <span v-if="REQUIREMENTS[metric[header.key]] === undefined">
                                -
                            </span>
                            <span v-else>
                                {{ $t(REQUIREMENTS[metric[header.key]].label_key) }}
                            </span>
                        </div>
                        <div v-else>
                            {{ metric[header.key] }}
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <sust-popup ref="optionsPopup"
            size="lg"
			title="Select an option"
            :footer="false"
        >
            <div v-if="selectedItem.metric != null"
                class="flex-all-centered-row options-wrapper"
            >
                <button class="sust-btn sust-btn-green"
                    @click="handleSelectAction(actionOptions.create, 'metric')"
                >
                    Create New Metric
                </button>
                <button v-if="selectedItem.header.is_self_table && !selectedItem.header.is_multiple && !selectedItem.header.is_foreign_schema && selectedItem.child.id != null"
                    class="sust-btn sust-btn-green"
                    @click="handleSelectAction(actionOptions.create, selectedItem.header.key)"
                >
                    Create New {{ $t(`metrics_admin_panel.${selectedItem.header.key}.title`) }}
                </button>
                <button class="sust-btn sust-btn-blue"
                    @click="handleSelectAction(actionOptions.update, 'metric')"
                >
                    Update "{{ selectedItem.metric.name }}"
                </button>
                <button class="sust-btn sust-btn-red"
                    @click="handleSelectAction(actionOptions.delete, 'metric')"
                >
                    Delete "{{ selectedItem.metric.name }}"
                </button>
                <button v-if="selectedItem.header.is_self_table && !selectedItem.header.is_multiple && !selectedItem.header.is_foreign_schema && selectedItem.child.id != null"
                    class="sust-btn sust-btn-blue"
                    @click="handleSelectAction(actionOptions.update, selectedItem.header.key)"
                >
                    Update {{ $t(`metrics_admin_panel.${selectedItem.header.key}.title`) }} "{{ selectedItem.child.name }}"
                </button>
                <button v-if="selectedItem.header.is_self_table && !selectedItem.header.is_multiple && !selectedItem.header.is_foreign_schema && selectedItem.child.id != null"
                    class="sust-btn sust-btn-red"
                    @click="handleSelectAction(actionOptions.delete, selectedItem.header.key)"
                >
                    Delete {{ $t(`metrics_admin_panel.${selectedItem.header.key}.title`) }} "{{ selectedItem.child.name }}"
                </button>
            </div>
        </sust-popup>

        <sust-popup ref="actionPopup"
            size="md"
			@onHide="setSelectedAction(null, null)"
            :header="false"
            :footer="false"
        >
            <div v-if="action.option != actionOptions.none && action.key != null"
                class="flex-all-centered-row"
            >
                <div v-if="action.option === actionOptions.delete"
                    class="flex-all-centered-col"
                >
                    <span>
                        Are you sure you want to delete
                        <span class="delete-confirm-emphasis">
                            <span v-if="action.key === 'metric'">
                                Metric "{{ selectedItem.metric.name }}"
                            </span>
                            <span v-else>
                                {{ $t(`metrics_admin_panel.${selectedItem.header.key}.title`) }}&nbsp;
                                "{{ selectedItem.child.name }}"
                            </span>
                        </span>
                        &nbsp;?
                    </span>

                    <div class="flex-all-centered-row popup-btns">
                        <button class="sust-btn sust-btn-red"
                            @click="$refs.actionPopup.startHideProcess()"
                        >
                            Cancel
                        </button>
                        <button class="sust-btn sust-btn-green"
                            @click="handleDeleteItem()"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
                <div v-else-if="action.option === actionOptions.update"
                    class="flex-all-centered-col"
                >
                    <div v-if="action.key === 'metric'" 
                        class="class-all-centered-row update-box"
                    >
                        <div v-for="h in headers.filter(x => x.metric_update_options.show)" :key="h.key"
                            class="update-field"
                        >
                            <label :for="`update_${h.key}`">
                                {{ $t(`metrics_admin_panel.${h.key}.title`) }}
                            </label>

                            <input v-if="h.metric_update_options.type === 'text'" 
                                type="text" :id="`update_${h.key}`"
                                v-model="selectedMetric[h.key]"
                            >
                            <select v-else-if="h.metric_update_options.type === 'select' && h.is_multiple === false" 
                                :id="`update_${h.key}`"
                                v-model="selectedMetric[h.key].id"
                            >
                                <option v-for="opt in dataForAction[h.key]" :key="opt.id"
                                    :value="opt.id"
                                >
                                    {{ opt.name }}
                                </option>
                            </select>
                            <button v-else-if="h.metric_update_options.type === 'select' && h.is_multiple === true" 
                                :id="`update_${h.key}`"
                                class="multiple-select-button"
                                @click="openMultiplesOptions(h.key)"
                            >
                                <div class="flex-all-centered-row">
                                    <span v-if="selectedMetric[h.key].length === 0">
                                        None
                                    </span>
                                    <span v-if="selectedMetric[h.key].length === 1">
                                        {{ selectedMetric[h.key][0].name }}
                                    </span>
                                    <span v-if="selectedMetric[h.key].length > 1">
                                        Multiple items...
                                    </span>
                                </div>
                            </button>
                            <select v-else-if="h.metric_update_options.type === 'requirement'" 
                                :id="`update_${h.key}`"
                                v-model="selectedMetric[h.key]"
                            >
                                <option v-for="opt in REQUIREMENTS" :key="opt.id"
                                    :value="opt.id"
                                >
                                    {{ $t(opt.label_key) }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div v-else-if="selectedChildHeader !== null" class="class-all-centered-row update-box">
                        <div v-if="selectedChildHeader.metric_update_options.parent_key !== null" 
                            class="update-field"
                        >
                            <label for="update_parent">
                                {{ $t(`metrics_admin_panel.${selectedChildHeader.metric_update_options.parent_key}.title`) }}
                            </label>

                            <select id="update_parent"
                                v-model="selectedMetric[selectedChildHeader.metric_update_options.parent_key].id"
                            >
                                <option v-for="opt in dataForAction.data" :key="opt.id"
                                    :value="opt.id"
                                >
                                    {{ opt.name }}
                                </option>
                            </select>
                        </div>
                        
                        <div class="update-field">
                            <label for="update_name">
                                Name
                            </label>

                            <input type="text" :id="`update_name`"
                                v-model="selectedItem.child.name"
                            >
                        </div>
                        
                        <div class="update-field">
                            <label for="update_description">
                                Description
                            </label>

                            <input type="text" :id="`update_description`"
                                v-model="selectedItem.child.description"
                            >
                        </div>
                    </div>

                    <div class="flex-all-centered-row popup-btns">
                        <button class="sust-btn sust-btn-red"
                            @click="$refs.actionPopup.startHideProcess()"
                        >
                            Cancel
                        </button>
                        <button class="sust-btn sust-btn-green"
                            @click="handleUpdateItem()"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
                <div v-else-if="action.option === actionOptions.create"
                    class="flex-all-centered-col"
                >
                    <div v-if="action.key === 'metric'" 
                        class="class-all-centered-row update-box"
                    >
                        <div v-for="h in headers.filter(x => x.metric_update_options.show)" :key="h.key"
                            class="update-field"
                        >
                            <label :for="`update_${h.key}`">
                                {{ $t(`metrics_admin_panel.${h.key}.title`) }}
                            </label>

                            <input v-if="h.metric_update_options.type === 'text'" 
                                type="text" :id="`update_${h.key}`"
                                v-model="newItemToCreate[h.key]"
                            >
                            <select v-else-if="h.metric_update_options.type === 'select' && h.is_multiple === false" 
                                :id="`update_${h.key}`"
                                v-model="newItemToCreate[h.key]"
                            >
                                <option v-for="opt in dataForAction[h.key]" :key="opt.id"
                                    :value="opt"
                                >
                                    {{ opt.name }}
                                </option>
                            </select>
                            <button v-else-if="h.metric_update_options.type === 'select' && h.is_multiple === true" 
                                :id="`update_${h.key}`"
                                class="multiple-select-button"
                                @click="openMultiplesOptions(h.key)"
                            >
                                <div class="flex-all-centered-row">
                                    <span v-if="newItemToCreate[h.key].length === 0">
                                        None
                                    </span>
                                    <span v-if="newItemToCreate[h.key].length === 1">
                                        {{ newItemToCreate[h.key][0].name }}
                                    </span>
                                    <span v-if="newItemToCreate[h.key].length > 1">
                                        Multiple items...
                                    </span>
                                </div>
                            </button>
                            <select v-else-if="h.metric_update_options.type === 'requirement'" 
                                :id="`update_${h.key}`"
                                v-model="newItemToCreate[h.key]"
                            >
                                <option v-for="opt in REQUIREMENTS" :key="opt.id"
                                    :value="opt.id"
                                >
                                    {{ $t(opt.label_key) }}
                                </option>
                            </select>
                        </div>
                    </div>
                    <div v-else-if="selectedChildHeader !== null" class="class-all-centered-row update-box">
                        <div v-if="selectedChildHeader.metric_update_options.parent_key !== null" 
                            class="update-field"
                        >
                            <label for="update_parent">
                                {{ $t(`metrics_admin_panel.${selectedChildHeader.metric_update_options.parent_key}.title`) }}
                            </label>

                            <select id="update_parent"
                                v-model="newItemToCreate[selectedChildHeader.metric_update_options.parent_key].id"
                            >
                                <option v-for="opt in dataForAction.data" :key="opt.id"
                                    :value="opt.id"
                                >
                                    {{ opt.name }}
                                </option>
                            </select>
                        </div>
                        
                        <div class="update-field">
                            <label for="update_name">
                                Name
                            </label>

                            <input type="text" :id="`update_name`"
                                v-model="newItemToCreate.name"
                            >
                        </div>
                        
                        <div class="update-field">
                            <label for="update_description">
                                Description
                            </label>

                            <input type="text" :id="`update_description`"
                                v-model="newItemToCreate.description"
                            >
                        </div>
                    </div>

                    <div class="flex-all-centered-row popup-btns">
                        <button class="sust-btn sust-btn-red"
                            @click="$refs.actionPopup.startHideProcess()"
                        >
                            Cancel
                        </button>
                        <button class="sust-btn sust-btn-green"
                            @click="handleCreateItem()"
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </div>
        </sust-popup>

        <sust-popup ref="updateMultiplesPopup"
            size="md"
            :header="false"
            :footer="false"
        >
            <div v-if="action.option === actionOptions.update && selectedMultiplesKey !== null && dataForAction !== null && dataForAction[refinedMultiplesKey] !== null"
                class="flex-all-centered-row multiples-selection"
            >
                <button v-for="opt in dataForAction[refinedMultiplesKey]" :key="opt.id"
                    class="multiples-selection-button"
                    :class="{
                        'selected': selectedItem.metric != null && selectedItem.metric[selectedMultiplesKey].some(x => x.id === opt.id)
                    }"
                    @click="handleClickMultiplesOption(opt)"
                >
                    {{ opt.name }}
                </button>
            </div>
            
            <div v-else-if="action.option === actionOptions.create && selectedMultiplesKey !== null && dataForAction !== null && dataForAction[refinedMultiplesKey] !== null"
                class="flex-all-centered-row multiples-selection"
            >
                <button v-for="opt in dataForAction[refinedMultiplesKey]" :key="opt.id"
                    class="multiples-selection-button"
                    :class="{
                        'selected': newItemToCreate != null && newItemToCreate[selectedMultiplesKey].some(x => x.id === opt.id)
                    }"
                    @click="handleClickMultiplesOption(opt)"
                >
                    {{ opt.name }}
                </button>
            </div>

            <div class="flex-all-centered-row popup-btns">
                <button class="sust-btn sust-btn-green"
                    @click="$refs.updateMultiplesPopup.startHideProcess()"
                >
                    OK
                </button>
            </div>
        </sust-popup>

        <sust-popup ref="bulkUpdatePopup"
            size="md"
            title="Bulk Update Actions"
            :footer="false"
        >
            <div class="flex-all-centered-col" style="align-items: stretch;">
                <input type="file" ref="fileInput" style="display: none;" accept=".xls,.xlsx" @change="handleFileSelect">
                <button class="sust-btn sust-btn-green" style="margin-bottom: 1em; background-color: var(--sustainfinity-faded-green)"
                    @click="$refs.fileInput.click()"
                >
                    {{ file ? file.name : 'Select file' }}
                </button>
                <button class="sust-btn sust-btn-yellow" style="margin-bottom: 1em;"
                    :disabled="bulkActionInProgress"
                    @click="handleBulkUpdate('init')"
                >
                    Bulk Create (This will delete the current data irrecoverably!)
                </button>
                <button class="sust-btn sust-btn-green"
                    :disabled="bulkActionInProgress || table.length === 0"
                    @click="handleBulkUpdate('update')"
                >
                    Bulk Update
                </button>
                <button class="sust-btn sust-btn-blue" style="margin-top: 1em;"
                    :disabled="bulkActionInProgress || table.length === 0"
                    @click="handleBulkUpdate('export')"
                >
                    Export Current Data
                </button>
            </div>
        </sust-popup>
    </div>
</template>

<style lang='scss' scoped>
    #metricsWrapper {
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: stretch;
        overflow-x: auto !important;
        overflow-y: auto !important;
        width: 100%;
        height: 100%;
        padding: 0 2em 2em 2em;

        &::-webkit-scrollbar {
            width: .6em;
            height: .6em;
        }
    }
    
    #metricsTable {
        box-sizing: border-box !important;
        height: fit-content;
        width: max-content; 
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        align-items: flex-start;

        filter: drop-shadow(0 .1em .1em gray);
    }

    .row {
        box-sizing: border-box !important;
        display: flex;
        flex-direction: row;
        justify-content: flex-start;
        align-items: stretch;
        flex-wrap: nowrap;
        
        background-color: whitesmoke;
        color: var(--sustainfinity-navy);

        &:last-of-type {
            border-bottom-left-radius: 1em;
            border-bottom-right-radius: 1em;
        }

        &:first-of-type, &:last-of-type {
            > .cell {
                &:first-of-type, &:last-of-type {
                    outline: 0 !important;
                }
            }
        }

        > .cell {
            cursor: pointer;
            transition: background-color 150ms;
            &:hover {
                background-color: rgba(218, 218, 218, 0.585);
            }
        }
    }

    .header {
        position: sticky;
        top: 0;
        z-index: 2;

        background-color: var(--sustainfinity-navy) !important;
        color: whitesmoke !important;
        border-top-left-radius: 1em;
        border-top-right-radius: 1em;

        > .cell {
            background-color: var(--sustainfinity-navy) !important;
            
            &:hover {
                background-color: var(--sustainfinity-navy) !important;
                text-decoration: underline;
            }
        }
    }

    .cell {
        box-sizing: border-box !important;
        padding: 12px;
        width: 125px;
        min-height: 60px;
        height: auto;
        outline: thin solid lightgray;
        display: flex;
        justify-content: center;
        align-items: center;
        text-wrap: wrap;
        word-break: break-all;
        text-align: center;
        overflow: hidden;
        font-size: .75vw;

        &:focus {
            z-index: 1;
            outline: thin solid var(--sustainfinity-green);
            box-shadow: 0 0 .5em .1em var(--sustainfinity-faded-green) inset;
        }
    }

    .options-wrapper {
        flex-wrap: wrap;
        justify-content: space-around;
        align-items: stretch;
        align-content: stretch;
        padding: 1em;

        > button {
            width: 45%;
            margin: 1em 0;
        }
    }

    .popup-btns {
        justify-content: flex-end;

        > button {
            margin-left: 1em;
        }
    }

    .update-box {
        width: 100%;
        display: flex;
        justify-content: space-between;
        align-items: stretch;
        align-content: space-between;
        flex-wrap: wrap;

        > .update-field {
            box-sizing: border-box;
            width: 45% !important;
            margin: .5em 0;

            > label {
                line-height: 1.5em;
            }

            > input, select, button {
                background-color: white;
                width: 100%;
                padding: .2em .5em 0 .2em;
                border-radius: .25em;
                border: 0 !important;
                outline: thin solid lightgray;
                
                &:focus {
                    outline: thin solid black !important;
                }
            }
        }
    }

    .multiple-select-button {
        position: relative;

    }

    .delete-confirm-emphasis {
        font-style: italic;
        font-weight: bold;
        color: var(--invalid-input);
    }

    .multiples-selection {
        flex-wrap: wrap;
        align-items: stretch;
        align-content: stretch;

        > .multiples-selection-button {
            margin: .5em 0;
            width: 42.5% !important;
            text-wrap: wrap;
            word-break: break-all;
            text-align: center;
            border: 0 !important;
            outline: thin solid lightgray;
            border-collapse: collapse;
            background-color: whitesmoke;

            transition: background-color 150ms, box-shadow 150ms;

            &:hover {
                background-color: lightgray;
            }

            &:focus {
                outline: thin solid black !important;
            }
        }

        > .selected {
            background-color: var(--sustainfinity-faded-green);
            box-shadow: 0 0 .5em .1em var(--sustainfinity-green) inset;
        }
    }

    .btn-pretend {
        outline: 0 !important;
        border: 0 !important;
        background-color: transparent !important;

        &:hover {
            text-decoration: underline;
        }
    }

</style>